import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { loginBlacApi } from '../../apis/authBlacApi';
import { LoginRequest } from '../../apis/authApi';
import { toast } from 'react-toastify';
import { Footer } from '../../components/Footer/Footer';
import skyAngelLogo from '../../img/skyangel.png';

const LoginBlack: React.FC = () => {
  const { register, handleSubmit, formState: { errors } } = useForm<LoginRequest>();
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const loginMutation = useMutation({
    mutationFn: loginBlacApi,
    onSuccess: (data) => {
      authContext.login(
        data.user, 
        data.token, 
        data.role,
        data.plataforma,
        data.onasset_token, 
        data.onasset_available,
        data.blac_token,
        data.blac_available,
        data.permisos,
        data.nivel
      );
      
      // Mostrar mensaje de éxito con información de Blac
      toast.success('Inicio de sesión exitoso');
      
      if (data.blac_available) {
        toast.info('Conexión con tags establecida', { autoClose: 3000 });
      } else {
        toast.warning('Conexión a tags no disponible temporalmente', { autoClose: 3000 });
      }

      // Redirigir a WelcomeBlack después del login exitoso
      setTimeout(() => {
        navigate('/welcome-black');
      }, 1000); // Pequeño delay para que se vean los toasts
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al iniciar sesión');
    },
  });

  const onSubmit = (data: LoginRequest) => {
    loginMutation.mutate(data);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    // Add interactive effects to form inputs
    const inputs = document.querySelectorAll('.form-input-black');
    
    const handleFocus = function(this: HTMLElement) {
      if (this.parentElement) {
        this.parentElement.style.transform = 'scale(1.02)';
        this.parentElement.style.transition = 'transform 0.3s ease';
      }
    };

    const handleBlur = function(this: HTMLElement) {
      if (this.parentElement) {
        this.parentElement.style.transform = 'scale(1)';
      }
    };

    inputs.forEach(input => {
      input.addEventListener('focus', handleFocus);
      input.addEventListener('blur', handleBlur);
    });

    return () => {
      inputs.forEach(input => {
        input.removeEventListener('focus', handleFocus);
        input.removeEventListener('blur', handleBlur);
      });
    };
  }, []);

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

    .login-page-black {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%);
      font-family: 'Inter', sans-serif;
      position: relative;
      overflow: hidden;
    }

    .login-page-black::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: 
        radial-gradient(circle at 20% 80%, rgba(0, 122, 255, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(0, 122, 255, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.02) 0%, transparent 50%);
      animation: backgroundShift 20s ease-in-out infinite;
    }

    @keyframes backgroundShift {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.8; }
    }

    .background-animation-black {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
      z-index: 1;
    }

    .particle-black {
      position: absolute;
      width: 2px;
      height: 2px;
      background: rgba(0, 122, 255, 0.3);
      border-radius: 50%;
      animation: floatBlack 8s infinite linear;
    }

    .particle-black:nth-child(1) { left: 10%; animation-delay: 0s; }
    .particle-black:nth-child(2) { left: 20%; animation-delay: 1s; }
    .particle-black:nth-child(3) { left: 30%; animation-delay: 2s; }
    .particle-black:nth-child(4) { left: 40%; animation-delay: 3s; }
    .particle-black:nth-child(5) { left: 50%; animation-delay: 4s; }
    .particle-black:nth-child(6) { left: 60%; animation-delay: 5s; }
    .particle-black:nth-child(7) { left: 70%; animation-delay: 0.5s; }
    .particle-black:nth-child(8) { left: 80%; animation-delay: 1.5s; }
    .particle-black:nth-child(9) { left: 90%; animation-delay: 2.5s; }

    @keyframes floatBlack {
      0% {
        transform: translateY(100vh) rotate(0deg);
        opacity: 0;
      }
      10% {
        opacity: 1;
      }
      90% {
        opacity: 1;
      }
      100% {
        transform: translateY(-100px) rotate(360deg);
        opacity: 0;
      }
    }

    /* Main container */
    .login-container-black {
      background: rgba(0, 0, 0, 0.95);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(0, 122, 255, 0.2);
      border-radius: 20px;
      padding: 40px;
      width: 100%;
      max-width: 420px;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(0, 122, 255, 0.1);
      position: relative;
      z-index: 10;
      animation: slideInBlack 0.8s ease-out;
    }

    @keyframes slideInBlack {
      from {
        opacity: 0;
        transform: translateY(50px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Black logo area */
    .logo-section-black {
      text-align: center;
      margin-bottom: 40px;
      animation: fadeInBlack 1s ease-out 0.3s both;
    }

    .skyangel-logo {
      width: 120px;
      height: auto;
      margin: 0 auto 20px auto;
      display: block;
      filter: drop-shadow(0 0 20px rgba(0, 122, 255, 0.3));
      animation: logoGlow 3s ease-in-out infinite alternate;
    }

    @keyframes logoGlow {
      from {
        filter: drop-shadow(0 0 20px rgba(0, 122, 255, 0.3));
      }
      to {
        filter: drop-shadow(0 0 30px rgba(0, 122, 255, 0.6));
      }
    }

    .black-logo {
      font-size: 32px;
      font-weight: 700;
      color: #ffffff;
      letter-spacing: 4px;
      margin-bottom: 10px;
      position: relative;
    }

    .black-logo::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 2px;
      background: linear-gradient(90deg, transparent, #007AFF, transparent);
      animation: glowBlack 2s ease-in-out infinite alternate;
    }

    @keyframes glowBlack {
      from {
        box-shadow: 0 0 5px rgba(0, 122, 255, 0.5);
      }
      to {
        box-shadow: 0 0 20px rgba(0, 122, 255, 0.8);
      }
    }

    .subtitle-black {
      color: #888888;
      font-size: 14px;
      font-weight: 400;
      letter-spacing: 2px;
      text-transform: uppercase;
    }

    /* Form styles */
    .login-form-black {
      animation: fadeInBlack 1s ease-out 0.6s both;
    }

    .form-group-black {
      position: relative;
      margin-bottom: 25px;
    }

    .password-input-container {
      position: relative;
    }

    .form-input-black {
      width: 100%;
      padding: 18px 20px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(0, 122, 255, 0.2);
      border-radius: 12px;
      color: #ffffff;
      font-size: 16px;
      font-weight: 400;
      transition: all 0.3s ease;
      outline: none;
      box-sizing: border-box;
    }

    .form-input-black.with-icon {
      padding-right: 55px;
    }

    .password-toggle {
      position: absolute;
      right: 18px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .password-toggle:hover {
      color: #007AFF;
      background: rgba(0, 122, 255, 0.1);
    }

    .password-toggle:focus {
      outline: none;
      color: #007AFF;
      background: rgba(0, 122, 255, 0.15);
    }

    .form-input-black {
      width: 100%;
      padding: 18px 20px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(0, 122, 255, 0.2);
      border-radius: 12px;
      color: #ffffff;
      font-size: 16px;
      font-weight: 400;
      transition: all 0.3s ease;
      outline: none;
      box-sizing: border-box;
    }

    .form-input-black:focus {
      background: rgba(255, 255, 255, 0.08);
      border-color: #007AFF;
      box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
      transform: translateY(-2px);
    }

    .form-input-black::placeholder {
      color: #666666;
      transition: all 0.3s ease;
    }

    .form-input-black:focus::placeholder {
      color: #888888;
      transform: translateX(5px);
    }

    .error-message-black {
      position: absolute;
      bottom: -20px;
      left: 20px;
      color: #ff6b6b;
      font-size: 12px;
      animation: fadeInBlack 0.3s ease;
    }

    /* Login button */
    .login-button-black {
      width: 100%;
      padding: 15px;
      background: linear-gradient(135deg, #007AFF 0%, #0056CC 100%);
      border: none;
      border-radius: 12px;
      color: #ffffff;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-top: 10px;
    }

    .login-button-black::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
      transition: left 0.5s ease;
    }

    .login-button-black:hover:not(:disabled) {
      background: linear-gradient(135deg, #0056CC 0%, #003D99 100%);
      transform: translateY(-2px);
      box-shadow: 0 10px 25px rgba(0, 122, 255, 0.3);
    }

    .login-button-black:hover:not(:disabled)::before {
      left: 100%;
    }

    .login-button-black:active:not(:disabled) {
      transform: translateY(0);
    }

    .login-button-black:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* Footer */
    .footer-black {
      text-align: center;
      margin-top: 30px;
      padding-top: 20px;
      border-top: 1px solid rgba(0, 122, 255, 0.1);
      animation: fadeInBlack 1s ease-out 1.2s both;
    }

    .footer-text-black {
      color: #666666;
      font-size: 12px;
      margin-bottom: 10px;
    }

    .device-tracker-info-black {
      color: #888888;
      font-size: 11px;
      font-weight: 300;
      letter-spacing: 1px;
    }

    /* Loading animation */
    .loading-black {
      display: inline-block;
      width: 20px;
      height: 20px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top: 2px solid #007AFF;
      border-radius: 50%;
      animation: spinBlack 1s linear infinite;
    }

    @keyframes spinBlack {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @keyframes fadeInBlack {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    /* Responsive design */
    @media (max-width: 480px) {
      .login-container-black {
        margin: 20px;
        padding: 30px 25px;
      }
      
      .black-logo {
        font-size: 28px;
        letter-spacing: 3px;
      }
    }

    /* Hover effects for the container */
    .login-container-black::before {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      background: linear-gradient(45deg, transparent, rgba(0, 122, 255, 0.1), transparent);
      border-radius: 22px;
      z-index: -1;
      opacity: 0;
      transition: opacity 0.3s ease;
    }

    .login-container-black:hover::before {
      opacity: 1;
    }
  `;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      
      <main style={{ flex: 1 }}>
        <div className="login-page-black">
          <div className="background-animation-black">
            <div className="particle-black"></div>
            <div className="particle-black"></div>
            <div className="particle-black"></div>
            <div className="particle-black"></div>
            <div className="particle-black"></div>
            <div className="particle-black"></div>
            <div className="particle-black"></div>
            <div className="particle-black"></div>
            <div className="particle-black"></div>
          </div>

        <div className="login-container-black">
          <div className="logo-section-black">
            <img 
              src={skyAngelLogo} 
              alt="Skyangel Logo" 
              className="skyangel-logo"
            />
            <div className="black-logo">Skyangel</div>
            <div className="subtitle-black">DEVICE MANAGEMENT</div>
          </div>

          <form className="login-form-black" onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group-black">
              <input
                {...register('correo', { 
                  required: 'El correo es requerido',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Correo electrónico inválido'
                  }
                })}
                type="email"
                className="form-input-black"
                placeholder="Correo electrónico"
              />
              {errors.correo && (
                <p className="error-message-black">{errors.correo.message}</p>
              )}
            </div>

            <div className="form-group-black">
              <div className="password-input-container">
                <input
                  {...register('contrasena', { required: 'La contraseña es requerida' })}
                  type={showPassword ? "text" : "password"}
                  className="form-input-black with-icon"
                  placeholder="Contraseña"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.contrasena && (
                <p className="error-message-black">{errors.contrasena.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="login-button-black"
            >
              {loginMutation.isPending ? (
                <div className="loading-black"></div>
              ) : (
                <span>Iniciar Sesión</span>
              )}
            </button>
          </form>

          <div className="footer-black">
            <div className="footer-text-black">Sistema de Gestión de Dispositivos</div>
            <div className="device-tracker-info-black">Plataforma de Monitoreo Avanzado</div>
          </div>
        </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LoginBlack;
